/**
 * Pre-sprint launch preparation — Bridge-created Today moves.
 * Creation/completion is never commercial evidence or a founder commitment.
 */

export const LAUNCH_PREP_IDEMPOTENCY_KEY = 'bridge:launch-prep:verify-dm';
export const OUTREACH_VOLUME_FLOOR = 3;

export function accountNeedsContactRoute(account) {
  if (!account) return true;
  const hasDm =
    !!account.decisionMakerKnown
    || !!String(account.primaryContact || '').trim()
    || !!String(account.decisionMaker || '').trim();
  const hasRoute =
    !!String(account.relationshipPath || '').trim()
    || !!String(account.nextAction || '').trim()
    || !!String(account.email || account.primaryEmail || '').trim();
  return !(hasDm && hasRoute);
}

export function priorityAccountsNeedingRoute(accounts = [], { limit = 5 } = {}) {
  return (accounts || [])
    .filter((a) => a.tier === 'Tier A' || a.tier === 'A')
    .filter(accountNeedsContactRoute)
    .slice(0, limit);
}

/**
 * @returns {{
 *   readyForLaunch: boolean,
 *   needsPrep: boolean,
 *   accountIds: string[],
 *   accountNames: string[],
 *   move: object | null,
 * }}
 */
export function deriveLaunchPrep(state, { today, sprintStart } = {}) {
  const accounts = state?.accounts || [];
  const priority = accounts.filter((a) => a.tier === 'Tier A' || a.tier === 'A');
  const needing = priorityAccountsNeedingRoute(accounts, { limit: 5 });
  const queueOk = !!(state?.ui?.founderQueueConfirmedAt || state?.ui?.founderQueueDeferred);
  const start = sprintStart || state?.settings?.sprint?.start || state?.settings?.sprintStart || '';

  if (!queueOk || priority.length === 0) {
    return { readyForLaunch: false, needsPrep: false, accountIds: [], accountNames: [], move: null, executionBegins: start };
  }

  if (needing.length === 0) {
    return {
      readyForLaunch: true,
      needsPrep: false,
      accountIds: [],
      accountNames: [],
      move: null,
      executionBegins: start,
      priorityCount: priority.length,
    };
  }

  const names = needing.map((a) => a.name || 'Unnamed').filter(Boolean);
  const move = {
    text: 'Verify the decision-maker and contact route for the first five named accounts.',
    workstream: 'Dubai',
    priority: 'High',
    expectedEvidence: 'OPS',
    evidenceCredit: 'none',
    dueDate: sprintStart || today || '',
    owner: 'Founder',
    status: 'Today',
    selectedForToday: true,
    todayRank: 1,
    aiPlanRank: 1,
    source: 'Bridge',
    preparation: true,
    bridgeCreated: true,
    idempotencyKey: LAUNCH_PREP_IDEMPOTENCY_KEY,
    whyNow: 'This prepares the first outreach wave without manufacturing pre-sprint activity.',
    expectedResult: 'Five accounts are ready for personalized outreach on execution start.',
    completionCondition: 'Decision-maker and contact route recorded for five priority accounts.',
    linkedLabel: names.slice(0, 5).join(', '),
    accountIds: needing.map((a) => a.id),
  };

  return {
    readyForLaunch: false,
    needsPrep: true,
    accountIds: needing.map((a) => a.id),
    accountNames: names,
    move,
    executionBegins: start,
    priorityCount: priority.length,
  };
}

export function hasOpenLaunchPrepMove(top3 = []) {
  return (top3 || []).some(
    (a) =>
      a.idempotencyKey === LAUNCH_PREP_IDEMPOTENCY_KEY
      && a.status !== 'Closed'
      && a.status !== 'Parked'
      && a.status !== 'Superseded',
  );
}

/** True when outreach volume is too low to diagnose commercial conversion. */
export function outreachVolumeInsufficient(counts = {}) {
  return (counts.contacted || 0) < OUTREACH_VOLUME_FLOOR;
}
